import { type PermissionedResource } from '@lib/permission';
import SPInspection from '@lib/spproject/SPInspection';
import { type Inspection } from '@lib/spproject/types';
import { type InspectionFilterArgs } from '@lib/inspections/filters';
import { handleApiResult, isServerError } from '@store/error';
import { apiSlice } from './apiSlice';
import type { RecentInspectionPage } from './statisticEndpoints';

export interface InspectionAuthor {
  _id: string;
  name: string;
  username: string;
}

export interface IInspection extends PermissionedResource {
  _id: string;
  /** Author of the inspection; drives the self-scope answer in `myPermissions`. */
  userId?: string;
  /**
   * The author's public identity, for display. A separate field from `userId` rather than a
   * populated one, because the id form is what the backend answers the self scope against.
   * Null when the referenced user no longer exists.
   */
  author?: InspectionAuthor | null;
  info: Inspection.Info;
  notes?: Inspection.PointOfInterest[];
  pointOfInterests?: Inspection.PointOfInterest[];
  /** Inspection-level attachments, normalized from the legacy `documents` key on receipt. */
  resources?: Inspection.Document[];
  // [key: string]: unknown;
}

/**
 * Field projection asked of the API. `notes`/`documents` are the legacy names of
 * `pointOfInterests`/`resources` and are still what old inspections are stored under,
 * so both names have to be requested to get either back.
 */
export type InspectionFields = ('notes' | 'pointOfInterests' | 'documents' | 'resources')[];

/** The report's fixed layouts, as the API names them. */
export const reportLayouts = ['classic', 'compact', 'executive'] as const;
export type ReportLayout = typeof reportLayouts[number];

/** What a report is drawn with: a fixed layout or a report design. The API refuses a request that names both. */
export type ReportRendering =
  | { layout: ReportLayout; designId?: never }
  | { designId: string; layout?: never };

export type InspectionReportRequest = { projectId: string; inspectionId: string; locale: string } & ReportRendering;

/**
 * Whether a report that was to be drawn with a design was refused the way a design the
 * reader can no longer use is: 404 once it has been deleted, 403 once it is not theirs to
 * read, which is what the next person at a shared browser gets, or someone who left the
 * team. An inspection that is gone, or not theirs, answers the same way, and the answer
 * does not say which of the two it is.
 */
export const isReportDesignMissing = (request: InspectionReportRequest, failure: unknown): boolean =>
  request.designId !== undefined && isServerError(failure) && [403, 404].includes(Number(failure.error.status));

/** A page of `GET /v1/inspections`: the filters, and which page of how many rows. */
export interface ListInspectionsArgs extends InspectionFilterArgs {
  page: number;
  limit: number;
}

/** An id list goes as one comma-separated parameter: the API keeps only the last of a repeated one. */
const idList = (ids?: string[]) => (ids?.length ? ids.join(',') : undefined);

/**
 * What the list's people and team filters can offer (`GET /v1/inspections/facets`): the
 * authors of every inspection the reader may read, and the teams owning their projects.
 */
export interface InspectionFacets {
  users: { id: string; name: string; username: string }[];
  teams: { id: string; name: string }[];
}

export const inspectionApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Every inspection the reader may read, across projects, a page at a time. Each page of
     * each set of filters is its own cache entry, so walking back to one already seen costs
     * no request. The rows carry the inspection tags, so an upload or a delete reads it anew,
     * and a `Project` tag: deleting, renaming, moving or sharing a project or a folder
     * invalidates the `Project` type as a whole, and the rows name projects and are read by
     * the folders they are in.
     */
    listInspections: builder.query<RecentInspectionPage, ListInspectionsArgs>({
      query: ({ page, limit, search, projectIds, userIds, teamIds, from, to }) => ({
        url: '/v1/inspections',
        // Undefined values are left out of the query string.
        params: {
          page,
          limit,
          search,
          project: idList(projectIds),
          user: idList(userIds),
          team: idList(teamIds),
          from,
          to,
        },
      }),
      providesTags: (result) => [
        { type: 'Inspection', id: 'LIST' },
        { type: 'Project', id: 'LIST' },
        ...(result?.data.map(({ inspectionId }) => ({ type: 'Inspection' as const, id: inspectionId })) ?? []),
      ],
      onQueryStarted: handleApiResult(),
    }),
    /**
     * Read once and kept, apart from the list's pages and filters. Read anew with the list,
     * and when a user or a team it names is renamed or deleted.
     */
    getInspectionFacets: builder.query<InspectionFacets, void>({
      query: () => '/v1/inspections/facets',
      providesTags: (result) => [
        { type: 'Inspection', id: 'LIST' },
        { type: 'Project', id: 'LIST' },
        ...(result?.users.map(({ id }) => ({ type: 'User' as const, id })) ?? []),
        ...(result?.teams.map(({ id }) => ({ type: 'Team' as const, id })) ?? []),
      ],
      onQueryStarted: handleApiResult(),
    }),
    getInspections: builder.query<IInspection[], { projectId: string; include?: InspectionFields }>({
      query: ({ projectId, include }) =>
        `/v1/projects/${projectId}/inspections${include ? `?include=${include.join(',')}` : ''}`,
      providesTags: (result, _error, { projectId }) => [
        { type: 'Inspection', id: 'LIST' },
        ...(result?.map(({ info }) => ({ type: 'Inspection' as const, id: info.id })) || []),
        { type: 'Project', id: projectId },
      ],
      transformResponse: (inspections: IInspection[]) => inspections.map(inspection => {
        SPInspection.transformInspectionForCompatibility(inspection as unknown as Inspection.Inspection);
        return inspection;
      }),
      onQueryStarted: handleApiResult(),
    }),
    getInspection: builder
      .query<IInspection, { projectId: string; inspectionId: string; include?: InspectionFields }>({
        query: ({ projectId, inspectionId, include }) =>
          `/v1/projects/${projectId}/inspections/${inspectionId}${include ? `?include=${include.join(',')}` : ''}`,
        providesTags: (_result, _error, { inspectionId }) => [
          { type: 'Inspection', id: inspectionId },
        ],
        transformResponse: (inspection: IInspection) => {
          SPInspection.transformInspectionForCompatibility(inspection as unknown as Inspection.Inspection);
          return inspection;
        },
        onQueryStarted: handleApiResult(),
      }),
    getInspectionFile: builder.query<string, { projectId: string; inspectionId: string }>({
      query: ({ projectId, inspectionId }) => ({
        url: `/v1/projects/${projectId}/inspections/${inspectionId}/file`,
        responseHandler: async (response) => response.ok ? response.blob() : response.json(),
      }),
      transformResponse: (blob: Blob) => (URL || webkitURL).createObjectURL(blob),
      onQueryStarted: handleApiResult(),
    }),
    getInspectionReport: builder.query<string, InspectionReportRequest>({
      query: ({ projectId, inspectionId, ...params }) => ({
        url: `/v1/projects/${projectId}/inspections/${inspectionId}/reports/pdf`,
        params,
        responseHandler: async (response) => response.ok ? response.blob() : response.json(),
      }),
      transformResponse: (blob: Blob) => (URL || webkitURL).createObjectURL(blob),
      onQueryStarted: async (arg, api) => {
        try {
          await api.queryFulfilled;
        } catch (error) {
          // Not shown: the download control answers this one itself, by asking for the classic
          // layout. If the design was gone, it says so; if the inspection was, that request
          // fails too and its error is the one to show.
          if (isReportDesignMissing(arg, error)) return;
        }

        return handleApiResult()(arg, api);
      },
    }),
    addInspection: builder.mutation<void, { projectId: string; formData: FormData }>({
      query: ({ projectId, formData }) => ({
        url: `/v1/projects/${projectId}/inspections`,
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: [{ type: 'Inspection', id: 'LIST' }],
      onQueryStarted: handleApiResult('Api_Inspection_Add_Successful'),
    }),
    updateInspection: builder.mutation<void, { projectId: string; inspectionId: string; formData: FormData }>({
      query: ({ projectId, inspectionId, formData }) => ({
        url: `/v1/projects/${projectId}/inspections/${inspectionId}`,
        method: 'PUT',
        body: formData,
      }),
      // The list too: a new file moves the inspection's date, and so its place on every page.
      invalidatesTags: (_result, _error, { inspectionId }) => [
        { type: 'Inspection', id: inspectionId },
        { type: 'Inspection', id: 'LIST' },
      ],
      onQueryStarted: handleApiResult('Api_Inspection_Update_Successful'),
    }),
    deleteInspection: builder.mutation<void, { projectId: string; inspectionId: string }>({
      query: ({ projectId, inspectionId }) => ({
        url: `/v1/projects/${projectId}/inspections/${inspectionId}`,
        method: 'DELETE',
      }),
      // The list too: every page after the one the inspection was on moves up by a row.
      invalidatesTags: (_result, _error, { inspectionId }) => [
        { type: 'Inspection', id: inspectionId },
        { type: 'Inspection', id: 'LIST' },
      ],
      onQueryStarted: handleApiResult('Api_Inspection_Delete_Successful'),
    }),
  }),
});

export const {
  useListInspectionsQuery,
  useGetInspectionFacetsQuery,
  useGetInspectionsQuery,
  useGetInspectionQuery,
  useLazyGetInspectionsQuery,
  useLazyGetInspectionFileQuery,
  useLazyGetInspectionReportQuery,
  useAddInspectionMutation,
  useUpdateInspectionMutation,
  useDeleteInspectionMutation,
} = inspectionApiSlice;
